import { DatePipe } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { DMS_ADMIN_ONLY_MESSAGE, SessionService } from '../../core/services/session.service';
import { ToastService } from '../../core/portal/toast.service';
import { PortalLocaleService, type PortalLang } from '../../core/portal/portal-locale.service';
import { LoginComponent } from '../login/login.component';
import {
  ANNOUNCEMENTS,
  DOMAIN,
  HERITAGE_IMAGES,
  HOW_IT_WORKS,
  LANDING_COPY,
  ORG,
  PLATFORM_MODULES,
} from './landing.content';
import { ICON_PATHS, MODULE_ICONS, STEP_ICONS, type IconName } from './landing-icons';

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [RouterLink, DatePipe, LoginComponent],
  templateUrl: './landing.component.html',
  styleUrl: './landing.component.css',
})
export class LandingComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly session = inject(SessionService);
  private readonly toast = inject(ToastService);
  readonly locale = inject(PortalLocaleService);

  readonly COPY = LANDING_COPY;
  readonly ORG = ORG;
  readonly logoUrl = '/assets/tenants/pda/logo.png';
  readonly PLATFORM_MODULES = PLATFORM_MODULES;
  readonly STEPS = HOW_IT_WORKS;
  readonly NOTICES = ANNOUNCEMENTS;
  readonly HERITAGE = HERITAGE_IMAGES;

  readonly lang = this.locale.lang;
  readonly highContrast = signal(false);
  readonly loginOpen = signal(false);
  readonly loginReturnUrl = signal('/enforcement/dashboard');
  readonly sessionMenuOpen = signal(false);

  private fontScale = 1;
  private querySub?: Subscription;

  ngOnInit(): void {
    this.querySub = this.route.queryParamMap.subscribe((params) => {
      const wantsLogin = params.get('login') === '1';
      const returnUrl = params.get('returnUrl') || '/enforcement/dashboard';

      if (wantsLogin && this.session.loggedIn()) {
        if (this.isDmsPath(returnUrl) && !this.session.canAccessDms()) {
          this.toast.showError(DMS_ADMIN_ONLY_MESSAGE);
          this.closeLogin();
          return;
        }
        void this.router.navigateByUrl(returnUrl);
        return;
      }

      this.loginOpen.set(wantsLogin);
      this.loginReturnUrl.set(returnUrl);
      document.body.style.overflow = wantsLogin ? 'hidden' : '';
    });
  }

  ngOnDestroy(): void {
    this.querySub?.unsubscribe();
    document.body.style.overflow = '';
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.sessionMenuOpen()) {
      this.closeSessionMenu();
      return;
    }
    if (this.loginOpen()) {
      this.closeLogin();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.session-wrap')) {
      this.closeSessionMenu();
    }
  }

  pick(en: string, hi: string): string {
    return this.locale.pick(en, hi);
  }

  setLang(next: PortalLang): void {
    this.locale.setLang(next);
  }

  openLogin(returnUrl = '/enforcement/dashboard'): void {
    void this.router.navigate(['/'], {
      queryParams: { login: '1', returnUrl },
    });
  }

  closeLogin(): void {
    void this.router.navigate(['/'], {
      queryParams: { login: null, returnUrl: null },
    });
  }

  scrollToModules(): void {
    document.getElementById('modules-h')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  openPlatformModule(key: (typeof PLATFORM_MODULES)[number]['key']): void {
    if (key === 'dms') {
      this.openDms();
      return;
    }
    this.openEnforcement();
  }

  openDms(): void {
    if (this.session.loggedIn()) {
      if (this.session.canAccessDms()) {
        void this.router.navigate(['/dms']);
        return;
      }
      this.toast.showError(DMS_ADMIN_ONLY_MESSAGE);
      return;
    }
    this.openLogin('/dms');
  }

  openEnforcement(): void {
    if (this.session.loggedIn()) {
      void this.router.navigateByUrl('/enforcement/dashboard');
      return;
    }
    this.openLogin('/enforcement/dashboard');
  }

  signOut(): void {
    this.session.logout();
  }

  toggleSessionMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.sessionMenuOpen.update((open) => !open);
  }

  closeSessionMenu(): void {
    this.sessionMenuOpen.set(false);
  }

  onPortalFromMenu(): void {
    this.closeSessionMenu();
    this.openPortal();
  }

  onSignOutFromMenu(): void {
    this.closeSessionMenu();
    this.signOut();
  }

  openPortal(): void {
    if (this.session.loggedIn()) {
      void this.router.navigateByUrl('/enforcement/dashboard');
      return;
    }
    this.openLogin('/enforcement/dashboard');
  }

  domainColor(domain: keyof typeof DOMAIN) {
    return DOMAIN[domain];
  }

  iconPath(name: IconName): string {
    return ICON_PATHS[name];
  }

  moduleIcon(domain: string): IconName {
    return MODULE_ICONS[domain] ?? 'records';
  }

  stepIcon(icon: string): IconName {
    return Object.prototype.hasOwnProperty.call(ICON_PATHS, icon) ? (icon as IconName) : 'apply';
  }

  fontDown(): void {
    this.setFontScale(this.fontScale - 0.08);
  }

  fontUp(): void {
    this.setFontScale(this.fontScale + 0.08);
  }

  fontReset(): void {
    this.setFontScale(1);
  }

  toggleContrast(): void {
    this.highContrast.update((v) => !v);
    document.documentElement.dataset['contrast'] = this.highContrast() ? 'high' : '';
  }

  private isDmsPath(url: string): boolean {
    return url === '/dms' || url.startsWith('/dms/');
  }

  private setFontScale(scale: number): void {
    this.fontScale = Math.min(1.25, Math.max(0.85, scale));
    document.documentElement.style.fontSize = `${this.fontScale * 100}%`;
  }
}
